import "./login-v3.css";
import googleIcon from "../../images/google-logo.svg";
import { useEffect, useRef, useState } from "react";
import trackEvent from "../../utils/analytics";
import { apiCall } from '../../utils/api';
import FirmAuth from '../../Firm/FirmAuth';

const LoginV3 = ({ darkMode, toggleDarkMode, onFirmLoginSuccess, hideFirmLogin = false }) => {
    const [isAndroidSource, setIsAndroidSource] = useState(false);
    const [isMahabharatCategory, setIsMahabharatCategory] = useState(false);
    const uuid = localStorage.getItem('user_id');
    const phoneInputRef = useRef(null);

    useEffect(() => {
        const urlObj = new URL(window.location.href);
        const params = new URLSearchParams(urlObj.search);
        const paramsObj = {};
        params.forEach((value, key) => {
            paramsObj[key] = value;
        });

        const utmSource = paramsObj.source;
        const categoryParam = paramsObj.category;
        setIsAndroidSource(utmSource === 'android');
        setIsMahabharatCategory(
            typeof categoryParam === 'string' && categoryParam.toLowerCase() === 'mahabharat'
        );

        if (paramsObj && Object.keys(paramsObj).length > 0) {
            localStorage.setItem('signupSource', JSON.stringify(paramsObj));
        }
    }, []);

    const [error, setError] = useState(null);
    const [loginType, setLoginType] = useState('user'); // 'user' or 'firm'

    // Phone/OTP state
    const [step, setStep] = useState('phone'); // 'phone' or 'otp'
    const [phone, setPhone] = useState('');
    const [otp, setOtp] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState('');
    const [otpTimer, setOtpTimer] = useState(0);

    // Timer effect for OTP resend
    useEffect(() => {
        let interval = null;
        if (otpTimer > 0) {
            interval = setInterval(() => {
                setOtpTimer(timer => timer - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [otpTimer]);

    // Ensure phone input is focusable & focused on initial render (and when returning to phone step)
    useEffect(() => {
        if (step !== 'phone') return;
        if (!phoneInputRef.current) return;
        phoneInputRef.current.focus();
    }, [step]);

    const setTokenCookies = (data) => {
        const accessTokenExpires = new Date(Date.now() + 1 * 864e5).toUTCString();
        const refreshTokenExpires = new Date(Date.now() + 7 * 864e5).toUTCString();
        document.cookie = `access_token=${data.access_token}; path=/; expires=${accessTokenExpires}`;
        document.cookie = `refresh_token=${data.refresh_token}; path=/; expires=${refreshTokenExpires}`;
        try {
            window?.AndroidBridge?.updateFcmToken();
        } catch (err) {
            console.error('Error updating FCM token via Android bridge:', err);
        }
    };

    const handlePhoneChange = (e) => {
        const value = e.target.value.replace(/\D/g, '');
        if (value.length <= 10) {
            setPhone(value);
            setFormError('');
        }
    };

    const handleOtpChange = (e) => {
        const value = e.target.value.replace(/\D/g, '');
        if (value.length <= 4) {
            setOtp(value);
            setFormError('');
        }
    };

    const isValidPhone = phone.length === 10;

    const handlePhoneSubmit = async (e) => {
        e.preventDefault();
        if (!isValidPhone) return;

        trackEvent('phone_otp_requested', {});
        setIsSubmitting(true);
        setFormError('');

        try {
            const payload = {
                phone_no: phone,
                signupSource: (() => {
                    try {
                        const stored = localStorage.getItem('signupSource');
                        return stored ? JSON.parse(stored) : null;
                    } catch (e) {
                        return null;
                    }
                })()
            };

            const response = await apiCall.post('/users/request/otp', JSON.stringify(payload));

            if (response?.status) {
                setStep('otp');
                setOtpTimer(60);
                trackEvent('phone_otp_sent', {});
            } else {
                setFormError(response.error_message || 'Failed to send OTP. Please try again.');
            }
        } catch (err) {
            console.error('OTP request error:', err);
            setFormError('Network error. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOtpSubmit = async (e) => {
        e.preventDefault();
        if (otp.length !== 4) return;

        trackEvent('phone_otp_verified_attempted', {});
        setIsSubmitting(true);
        setFormError('');

        try {
            const payload = {
                phone_no: phone,
                otp: otp,
                name: null,
                signupSource: (() => {
                    try {
                        const stored = localStorage.getItem('signupSource');
                        return stored ? JSON.parse(stored) : null;
                    } catch (e) {
                        return null;
                    }
                })()
            };

            const response = await apiCall.post('/users/verify/otp', JSON.stringify(payload));

            if (response?.status) {
                setTokenCookies(response?.data);
                
                // Store is_new_user flag from token response for onboarding check
                if (response?.data?.is_new_user !== undefined) {
                    try {
                        localStorage.setItem('is_new_user', JSON.stringify(response.data.is_new_user));
                    } catch (e) {
                        // ignore storage errors
                    }
                }
                
                trackEvent('phone_login_success', {});
                
                // Redirect to stored path (e.g. /nishantkumar) or reload current page
                const redirectPath = localStorage.getItem('postLoginRedirect');
                if (redirectPath) {
                    localStorage.removeItem('postLoginRedirect');
                    window.location.href = redirectPath;
                } else {
                    window.location.reload();
                }
            } else {
                setFormError(response.error_message || 'Invalid OTP. Please try again.');
            }
        } catch (err) {
            console.error('OTP verification error:', err);
            setFormError('Network error. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleResendOtp = async () => {
        if (otpTimer > 0) return;

        trackEvent('phone_otp_resend_attempted', {});
        setIsSubmitting(true);
        setFormError('');

        try {
            const payload = {
                phone_no: phone,
                signupSource: (() => {
                    try {
                        const stored = localStorage.getItem('signupSource');
                        return stored ? JSON.parse(stored) : null;
                    } catch (e) {
                        return null;
                    }
                })()
            };

            const response = await apiCall.post('/users/request/otp', JSON.stringify(payload));

            if (response?.status) {
                setOtpTimer(60);
                setOtp('');
                trackEvent('phone_otp_resent', {});
            } else {
                setFormError(response.error_message || 'Failed to resend OTP. Please try again.');
            }
        } catch (err) {
            console.error('OTP resend error:', err);
            setFormError('Network error. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const goBackToPhone = () => {
        setStep('phone');
        setOtp('');
        setFormError('');
        setOtpTimer(0);
    };

    // Register a global callback so the Android app can return the Google token
    useEffect(() => {
        window.onGoogleSignInResult = async (googleAccessToken, idToken) => {
            if (!googleAccessToken && !idToken) {
                console.error('Google sign-in: no token received from Android');
                setFormError('Google sign-in failed. Please try again.');
                return;
            }
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/users/generate/tokens`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            google_access_token: googleAccessToken || null,
                            id_token: idToken || null,
                            uuid: localStorage.getItem('user_id'),
                            signupSource: (() => {
                                try {
                                    const stored = localStorage.getItem('signupSource');
                                    return stored ? JSON.parse(stored) : null;
                                } catch (e) { return null; }
                            })()
                        })
                    }
                );
                const data = await response.json();
                if (data.status && data.data) {
                    setTokenCookies(data.data);
                    trackEvent('google_signin_success', {});
                    window.location.reload();
                } else {
                    setFormError(data.message || 'Authentication failed. Please try again.');
                }
            } catch (err) {
                console.error('Google sign-in token exchange failed:', err);
                setFormError('Something went wrong. Please try again.');
            }
        };

        return () => {
            delete window.onGoogleSignInResult;
        };
    }, []);

    const onGoogleSignIn = async () => {
        console.log("Android sign in triggered");

        if (window?.AndroidBridge?.triggerGoogleLogin) {
            window.AndroidBridge.triggerGoogleLogin();
            return;
        } else {
            throw new Error("AndroidBridge not available");
        }
    };

    const handleGoogleSignIn = async () => {
        trackEvent('google_signin_attempted', {});

        try {
            await onGoogleSignIn();
            return;
        } catch (error) {
            console.log("Android sign-in not available, proceeding with web OAuth");
        }

        const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
        const redirectUri = encodeURIComponent(process.env.REACT_APP_REDIRECT_URI);
        const scope = encodeURIComponent('email profile');
        const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=token&scope=${scope}`;
        window.location.href = googleAuthUrl;
    };

    if (error) {
        throw error;
    }
    
    // Show Google sign-in for all users except Mahabharat category
    // For Android users, the handleGoogleSignIn function will use AndroidBridge
    const shouldShowGoogleSignIn = !isMahabharatCategory;

    const handleFirmLoginSuccess = (userType) => {
        if (onFirmLoginSuccess) {
            onFirmLoginSuccess(userType);
        } else {
            window.location.reload();
        }
    };

    // If firm login is selected, render FirmAuth component inside an overlay
    if (!hideFirmLogin && loginType === 'firm') {
        return (
            <div className="firm-auth-overlay">
                <FirmAuth 
                    onLoginSuccess={handleFirmLoginSuccess}
                    onBack={() => setLoginType('user')}
                />
            </div>
        );
    }

    return (
        <div className="login-v3">
        <div className="login-v3-container">
                {!hideFirmLogin && (
                <div className="login-v3-type-toggle">
                    <button 
                        className={`login-v3-type-btn ${loginType === 'user' ? 'active' : ''}`}
                        onClick={() => setLoginType('user')}
                    >
                        User
                    </button>
                    <button 
                        className={`login-v3-type-btn ${loginType === 'firm' ? 'active' : ''}`}
                        onClick={() => setLoginType('firm')}
                    >
                        Firm
                    </button>
                </div>
                )}
                {/* Phone Input Section */}
                <div className="login-v3-section">
                    {step === 'phone' ? (
                        <form onSubmit={handlePhoneSubmit} className="login-v3-phone-form">
                            <div className="login-v3-phone-input-wrapper">
                                <span className="login-v3-country-code">+91</span>
                                <input
                                    type="tel"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    value={phone}
                                    onChange={handlePhoneChange}
                                    placeholder="Your mobile number"
                                    maxLength="10"
                                    className="login-v3-phone-input"
                                    autoComplete="tel"
                                    autoFocus
                                    ref={phoneInputRef}
                                />
                </div>

                            {formError && <div className="login-v3-form-error">{formError}</div>}

                            <button
                                type="submit"
                                className={`login-v3-continue-btn ${isValidPhone ? 'active' : 'disabled'}`}
                                disabled={!isValidPhone || isSubmitting}
                            >
                                {isSubmitting ? 'Sending...' : 'Send OTP'}
                            </button>

                                {/* Google Sign-In hidden for now */}
                            
                        </form>
                    ) : (
                        <form onSubmit={handleOtpSubmit} className="login-v3-otp-form">
                            <p className="login-v3-input-helper">
                                Enter the 4-digit OTP sent to <strong>+91 {phone}</strong>
                            </p>

                            <input
                                type="tel"
                                inputMode="numeric"
                                value={otp}
                                onChange={handleOtpChange}
                                placeholder="Enter OTP"
                                maxLength="4"
                                className="login-v3-otp-input"
                                autoComplete="one-time-code"
                            />

                            {formError && <div className="login-v3-form-error">{formError}</div>}

                            <button
                                type="submit"
                                className={`login-v3-continue-btn ${otp.length === 4 ? 'active' : 'disabled'}`}
                                disabled={otp.length !== 4 || isSubmitting}
                            >
                                {isSubmitting ? 'Verifying...' : 'Verify OTP'}
                            </button>

                            <div className="login-v3-otp-actions">
                                <button
                                    type="button"
                                    className="login-v3-text-btn"
                                    onClick={handleResendOtp}
                                    disabled={otpTimer > 0 || isSubmitting}
                                >
                                    {otpTimer > 0 ? `Resend OTP in ${otpTimer}s` : 'Resend OTP'}
                                </button>
                                <button
                                    type="button"
                                    className="login-v3-text-btn"
                                    onClick={goBackToPhone}
                                    disabled={isSubmitting}
                                >
                                    Change number
                                </button>
                    </div>
                        </form>
                    )}
            </div>
        </div>
        </div>
    );
};

export default LoginV3;